// src/pages/Register.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/Auth.css';

const Register = () => {
  // Company Information
  const [companyName, setCompanyName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [industry, setIndustry] = useState('');
  
  // Admin Information
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPhone, setAdminPhone] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const { register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validation
    if (!companyName || !companyEmail || !adminFirstName || !adminLastName || !adminEmail || !adminPassword) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    if (adminPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (adminPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    // Prepare registration data matching backend format
    const registrationData = {
      companyName: companyName,
      companyEmail: companyEmail,
      companyPhone: companyPhone,
      address: address,
      city: city,
      province: province,
      postalCode: postalCode,
      industry: industry,
      firstName: adminFirstName,
      lastName: adminLastName,
      email: adminEmail,
      phoneNumber: adminPhone,
      password: adminPassword
    };

    const result = await register(registrationData);
    
    if (result.success) {
      setSuccess(result.message);
      // Redirect to login after 4 seconds
      setTimeout(() => {
        navigate('/login');
      }, 4000);
    } else {
      setError(result.error || 'Registration failed. Please try again.');
    }
    
    setLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card register-card">
        <div className="auth-header">
          <h1>Inventory System</h1>
          <h2>Register Your Company</h2>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && (
          <div className="success-message">
            <strong>✓ {success}</strong>
            <p style={{ marginTop: '10px', fontSize: '14px' }}>
              Redirecting to login page in a few seconds...
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          {/* Company Information Section */}
          <div className="form-section">
            <h3 className="section-title">Company Information</h3>
            
            <div className="form-group">
              
              <input
                type="text"
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder=" "
                disabled={loading}
                required
              />
              <label htmlFor="companyName">Company Name *</label>
            </div>

            <div className="form-row">
              <div className="form-group">
                
                <input
                  type="email"
                  id="companyEmail"
                  value={companyEmail}
                  onChange={(e) => setCompanyEmail(e.target.value)}
                  placeholder=" "
                  disabled={loading}
                  required
                />
                <label htmlFor="companyEmail">Company Email *</label>
              </div>

              <div className="form-group">
                
                <input
                  type="tel"
                  id="companyPhone"
                  value={companyPhone}
                  onChange={(e) => setCompanyPhone(e.target.value)}
                  placeholder=" "
                  disabled={loading}
                />
                <label htmlFor="companyPhone">Company Phone</label>
              </div>
            </div>

            <div className="form-group">
              <input
                type="text"
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder=" "
                disabled={loading}
              />
              <label htmlFor="address">Street address</label>
            </div>

            <div className="form-row">
              <div className="form-group">
                <input
                  type="text"
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder=" "
                  disabled={loading}
                />
                <label htmlFor="city">City</label>
              </div>

              <div className="form-group">
                <input
                  type="text"
                  id="province"
                  value={province}
                  onChange={(e) => setProvince(e.target.value)}
                  placeholder=" "
                  disabled={loading}
                />
                <label htmlFor="province">Province</label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <input
                  type="text"
                  id="postalCode"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder=" "
                  disabled={loading}
                />
                <label htmlFor="postalCode">Postal code</label>
              </div>
            </div>

            <div className="form-group">
              <input
                type="text"
                id="industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder=" "
                disabled={loading}
              />
              <label htmlFor="industry">Industry</label>
            </div>
          </div>

          {/* Admin Information Section */}
          <div className="form-section">
            <h3 className="section-title">Admin Account Information</h3>
            
            <div className="form-row">
              <div className="form-group">
                <input
                  type="text"
                  id="adminFirstName"
                  value={adminFirstName}
                  onChange={(e) => setAdminFirstName(e.target.value)}
                  placeholder=" "
                  disabled={loading}
                  required
                />
                <label htmlFor="adminFirstName">First Name *</label>
              </div>

              <div className="form-group">
                <input
                  type="text"
                  id="adminLastName"
                  value={adminLastName}
                  onChange={(e) => setAdminLastName(e.target.value)}
                  placeholder=" "
                  disabled={loading}
                  required
                />
                <label htmlFor="adminLastName">Last Name *</label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <input
                  type="email"
                  id="adminEmail"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder=" "
                  disabled={loading}
                  required
                />
                <label htmlFor="adminEmail">Admin Email *</label>
              </div>

              <div className="form-group">
                <input
                  type="tel"
                  id="adminPhone"
                  value={adminPhone}
                  onChange={(e) => setAdminPhone(e.target.value)}
                  placeholder=" "
                  disabled={loading}
                />
                <label htmlFor="adminPhone">Admin Phone</label>
              </div>
            </div>

            <div className="form-group">
              <input
                type="password"
                id="adminPassword"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                placeholder=" "
                disabled={loading}
                required
              />
              <label htmlFor="adminPassword">Password *</label>
            </div>

            <div className="form-group">
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder=" "
                disabled={loading}
                required
              />
              <label htmlFor="confirmPassword">Confirm Password *</label>
            </div>
          </div>

          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Company Account'}
          </button>
        </form>

        <div className="auth-footer">
          <p>Already have an account? <Link to="/login">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
};

export default Register;